/**
 * Operator Desk workspace geofences.
 * Circle around a pin when the operator did not drop a work-area square;
 * polygon when they did. Radii match haul pad (150 m) / street (75 m).
 */
import { isOilfieldBidSite } from './bidListing';

export const OPERATOR_DESK_OILFIELD_GEOFENCE_M = 150;
export const OPERATOR_DESK_COMMERCIAL_GEOFENCE_M = 75;
export const OPERATOR_DESK_GEOFENCE_HYSTERESIS = 1.2;
export const OPERATOR_DESK_GEO_DEBOUNCE_MS = 60_000;
export const OPERATOR_DESK_GEO_PING_MS = 30_000;

export type OperatorDeskGeoEventType = 'entered' | 'left';

export type OperatorDeskLatLng = { lat: number; lng: number };

export type OperatorDeskFence = {
  latitude: number;
  longitude: number;
  radiusMeters: number;
  polygon: OperatorDeskLatLng[] | null;
};

export function operatorDeskWorkspaceRadiusM(input?: {
  siteType?: unknown;
  apiNumber?: unknown;
}): number {
  if (input?.siteType != null && String(input.siteType).trim()) {
    return isOilfieldBidSite(input.siteType)
      ? OPERATOR_DESK_OILFIELD_GEOFENCE_M
      : OPERATOR_DESK_COMMERCIAL_GEOFENCE_M;
  }
  const api = String(input?.apiNumber || '').replace(/\D/g, '');
  return api.length >= 8
    ? OPERATOR_DESK_OILFIELD_GEOFENCE_M
    : OPERATOR_DESK_COMMERCIAL_GEOFENCE_M;
}

export function finiteOperatorDeskCoord(
  lat: unknown,
  lng: unknown,
): OperatorDeskLatLng | null {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (Math.abs(a) > 90 || Math.abs(b) > 180) return null;
  return { lat: a, lng: b };
}

export function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function normalizeOperatorDeskPolygon(raw: unknown): OperatorDeskLatLng[] | null {
  if (typeof raw === 'string') {
    try {
      return normalizeOperatorDeskPolygon(JSON.parse(raw));
    } catch {
      return null;
    }
  }
  if (!Array.isArray(raw) || raw.length < 4) return null;
  const out: OperatorDeskLatLng[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const at = finiteOperatorDeskCoord(rec.lat ?? rec.latitude, rec.lng ?? rec.longitude);
    if (!at) continue;
    const prev = out[out.length - 1];
    if (prev && prev.lat === at.lat && prev.lng === at.lng) continue;
    out.push(at);
  }
  if (out.length < 4) return null;
  const first = out[0];
  const last = out[out.length - 1];
  if (first.lat !== last.lat || first.lng !== last.lng) out.push({ ...first });
  return out.length >= 5 ? out : null;
}

export function operatorDeskFenceSiteKey(input: {
  facilityId?: unknown;
  apiNumber?: unknown;
  latitude?: unknown;
  longitude?: unknown;
  fallback?: string;
}): string {
  const facility = String(input.facilityId || '').trim();
  if (facility) return facility.slice(0, 80);
  const api = String(input.apiNumber || '').replace(/\D/g, '');
  if (api.length >= 8) return `api:${api}`;
  const at = finiteOperatorDeskCoord(input.latitude, input.longitude);
  if (at) return `${at.lat.toFixed(5)},${at.lng.toFixed(5)}`;
  return String(input.fallback || 'primary').slice(0, 80);
}

export function polygonCentroid(path: OperatorDeskLatLng[]): OperatorDeskLatLng {
  let lat = 0;
  let lng = 0;
  const n = Math.max(1, path.length - (path[0] && path[path.length - 1]
    && path[0].lat === path[path.length - 1].lat
    && path[0].lng === path[path.length - 1].lng ? 1 : 0));
  const pts = path.slice(0, n);
  for (const p of pts) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / pts.length, lng: lng / pts.length };
}

export function polygonRadiusMeters(path: OperatorDeskLatLng[]): number {
  const c = polygonCentroid(path);
  let max = 0;
  for (const p of path) {
    max = Math.max(max, haversineMeters(c.lat, c.lng, p.lat, p.lng));
  }
  return Math.max(8, max);
}

/** Ray-cast even-odd. Closed rings OK. */
export function pointInPolygon(lat: number, lng: number, path: OperatorDeskLatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const xi = path[i].lng;
    const yi = path[i].lat;
    const xj = path[j].lng;
    const yj = path[j].lat;
    const intersect = ((yi > lat) !== (yj > lat))
      && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export function operatorDeskPointInsideFence(
  lat: number,
  lng: number,
  fence: OperatorDeskFence,
): boolean {
  return haversineMeters(lat, lng, fence.latitude, fence.longitude) <= fence.radiusMeters;
}

export function operatorDeskPointOutsideForLeave(
  lat: number,
  lng: number,
  fence: OperatorDeskFence,
): boolean {
  return haversineMeters(lat, lng, fence.latitude, fence.longitude)
    > fence.radiusMeters * OPERATOR_DESK_GEOFENCE_HYSTERESIS;
}

export function parseOperatorDeskFenceRow(row: {
  latitude?: unknown;
  longitude?: unknown;
  radius_meters?: unknown;
  polygon_json?: unknown;
}): OperatorDeskFence | null {
  const at = finiteOperatorDeskCoord(row.latitude, row.longitude);
  if (!at) return null;
  const radius = Number(row.radius_meters);
  return {
    latitude: at.lat,
    longitude: at.lng,
    radiusMeters: Number.isFinite(radius) && radius > 0
      ? radius
      : OPERATOR_DESK_OILFIELD_GEOFENCE_M,
    polygon: normalizeOperatorDeskPolygon(row.polygon_json),
  };
}

export function operatorDeskGeoUpdateNote(
  eventType: OperatorDeskGeoEventType,
  opts?: { company?: string | null; site?: string | null } | string | null,
): string {
  const company = typeof opts === 'object' && opts
    ? String(opts.company || '').trim()
    : '';
  const site = typeof opts === 'string' || opts == null
    ? String(opts || '').trim()
    : String(opts.site || '').trim();
  const name = company || 'Contractor';
  const verb = eventType === 'entered' ? 'entered' : 'left';
  return site ? `${name} ${verb} · ${site}` : `${name} ${verb}`;
}

export type OperatorDeskVisit = {
  company: string;
  event: OperatorDeskGeoEventType;
  site: string | null;
};

export function parseOperatorDeskVisitNote(note: string | null | undefined): OperatorDeskVisit | null {
  const n = String(note || '').trim();
  if (!n) return null;
  const match = n.match(/^(.*?)\s+(entered|left)(?:\s+workspace)?(?:\s*·\s*(.*))?$/i);
  if (!match) return null;
  const company = match[1].trim() || 'Contractor';
  const event = match[2].toLowerCase() === 'left' ? 'left' : 'entered';
  const site = match[3] ? match[3].trim() : null;
  return { company, event, site: site || null };
}

export function formatOperatorDeskVisitAt(raw: unknown): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const iso = /T/.test(s) ? s : `${s.replace(' ', 'T')}${s.endsWith('Z') ? '' : 'Z'}`;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function operatorDeskVisitLine(update: {
  note?: string | null;
  createdAt?: string | null;
}): { title: string; meta: string } | null {
  const parsed = parseOperatorDeskVisitNote(update.note || null);
  if (!parsed) return null;
  const verb = parsed.event === 'entered' ? 'Entered' : 'Left';
  const when = formatOperatorDeskVisitAt(update.createdAt);
  return {
    title: parsed.company,
    meta: [verb, when].filter(Boolean).join(' · '),
  };
}
